// frontend/src/pages/HowWeAreDifferentPage/HowWeAreDifferentPage.jsx

import React, { useCallback, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, List, ListItem, Avatar } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import GavelIcon from '@mui/icons-material/Gavel';
import PolicyIcon from '@mui/icons-material/Policy';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { Helmet } from 'react-helmet-async';
import UniversalNav from '../../components/UniversalNav/UniversalNav';
import './HowWeAreDifferentPage.css';

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

const HowWeAreDifferentPage = () => {
    const { t } = useTranslation();
    const [isScrolled, setIsScrolled] = useState(false);
    const containerRef = useRef(null);

    const handleScroll = (e) => {
        if (e.target.scrollTop > 20) {
            setIsScrolled(true);
        } else {
            setIsScrolled(false);
        }
    };

    const particlesInit = useCallback(async (engine) => {
        await loadSlim(engine);
    }, []);

    const particlesConfig = {
        background: { color: { value: 'transparent' } },
        fpsLimit: 120,
        interactivity: {
            events: { onHover: { enable: true, mode: 'repulse' }, resize: true },
            modes: { repulse: { distance: 100, duration: 0.4 } },
        },
        particles: {
            color: { value: '#ffffff' },
            links: { color: '#4fc3f7', distance: 150, enable: true, opacity: 0.4, width: 1 },
            move: { direction: 'none', enable: true, outModes: { default: 'bounce' }, random: false, speed: 1, straight: false },
            number: { density: { enable: true, area: 800 }, value: 80 },
            opacity: { value: 0.5 },
            shape: { type: 'circle' },
            size: { value: { min: 1, max: 3 } },
        },
        detectRetina: true,
    };

    return (
        <div 
            ref={containerRef}
            onScroll={handleScroll}
            className={`hwd-page-container ${isScrolled ? 'scrolled' : ''}`}
        >
            <Particles id="tsparticles-hwd" init={particlesInit} options={particlesConfig} />
            <Helmet>
                <title>{t('howWeAreDifferentPage.helmet.title')}</title>
                <meta name="description" content={t('howWeAreDifferentPage.helmet.description')} />
            </Helmet>
            
            <div className="hwd-nav-container">
                <UniversalNav />
            </div>

            <main className="hwd-content-wrapper">
                <Box className="hwd-glass-container">

                    {/* Section 1: Hero */}
                    <Box className="hwd-header">
                        <Typography variant="h3" component="h1">
                            {t('howWeAreDifferentPage.newHero.title')}
                        </Typography>
                        <Typography variant="subtitle1" component="p">
                            {t('howWeAreDifferentPage.newHero.subtitle')}
                        </Typography>
                    </Box>

                    {/* Section 2: The Regulatory Landscape */}
                    <Box className="hwd-section">
                        <Typography variant="h4" component="h2" align="center" gutterBottom>
                            {t('howWeAreDifferentPage.regulation.title')}
                        </Typography>
                        <Typography paragraph align="center" sx={{ maxWidth: '80ch', mx: 'auto', mb: 4 }}>
                           {t('howWeAreDifferentPage.regulation.intro')}
                        </Typography>
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={itemVariants}>
                            <Paper className="hwd-info-box" elevation={3}>
                                <Avatar className="hwd-info-icon"><PolicyIcon/></Avatar>
                                <Typography variant="h6" component="h3">{t('howWeAreDifferentPage.regulation.c2pa.title')}</Typography>
                                <Typography paragraph sx={{ mt: 1 }}>{t('howWeAreDifferentPage.regulation.c2pa.p1')}</Typography>
                                <Typography paragraph sx={{ mt: 1 }}>{t('howWeAreDifferentPage.regulation.c2pa_limitations.p1')}</Typography>
                                <List sx={{ listStyleType: 'disc', pl: 4, mt: 0 }}>
                                    <ListItem sx={{ display: 'list-item', p: 0.5 }}>{t('howWeAreDifferentPage.regulation.c2pa_limitations.l1')}</ListItem>
                                    <ListItem sx={{ display: 'list-item', p: 0.5 }}>{t('howWeAreDifferentPage.regulation.c2pa_limitations.l2')}</ListItem>
                                    <ListItem sx={{ display: 'list-item', p: 0.5 }}>{t('howWeAreDifferentPage.regulation.c2pa_limitations.l3')}</ListItem>
                                </List>
                            </Paper>
                        </motion.div>
                    </Box>

                    {/* Section 3: The Vetra AI Solution */}
                     <Box className="hwd-section">
                        <Typography variant="h4" component="h2" align="center" gutterBottom>
                             {t('howWeAreDifferentPage.solution.title')}
                        </Typography>
                        <Typography paragraph align="center" sx={{ maxWidth: '80ch', mx: 'auto', mb: 4 }}>
                            {t('howWeAreDifferentPage.solution.intro')}
                        </Typography>
                        <div className="hwd-solution-grid">
                            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.5 }} variants={itemVariants}>
                                <Paper className="hwd-info-box">
                                    <Avatar className="hwd-info-icon"><VerifiedUserIcon /></Avatar>
                                    <Typography variant="h6" component="h3">{t('howWeAreDifferentPage.solution.cards.security.title')}</Typography>
                                    <Typography variant="body2">{t('howWeAreDifferentPage.solution.cards.security.text')}</Typography>
                                </Paper>
                            </motion.div>
                            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.5 }} variants={itemVariants}>
                                <Paper className="hwd-info-box">
                                    <Avatar className="hwd-info-icon"><GavelIcon /></Avatar>
                                    <Typography variant="h6" component="h3">{t('howWeAreDifferentPage.solution.cards.proof.title')}</Typography>
                                    <Typography variant="body2">{t('howWeAreDifferentPage.solution.cards.proof.text')}</Typography>
                                </Paper>
                            </motion.div>
                        </div>
                    </Box>

                    {/* Section 4: Comparison Table */}
                    <Box className="hwd-section">
                        <Typography variant="h4" component="h2" align="center" gutterBottom>
                            {t('howWeAreDifferentPage.comparison.title')}
                        </Typography>
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={itemVariants}>
                            <TableContainer component={Paper} className="comparison-table">
                                <Table aria-label="comparison table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('howWeAreDifferentPage.comparison.table.header.capability')}</TableCell>
                                            <TableCell align="center">{t('howWeAreDifferentPage.comparison.table.header.adVerification')}</TableCell>
                                            <TableCell align="center">{t('howWeAreDifferentPage.comparison.table.header.provenance')}</TableCell>
                                            <TableCell align="center" className="vetra-ai-column">{t('howWeAreDifferentPage.comparison.table.header.vetraAI')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell component="th" scope="row">{t('howWeAreDifferentPage.comparison.table.body.analyzesContent')}</TableCell>
                                            <TableCell align="center"><CancelIcon className="icon-cross" /></TableCell>
                                            <TableCell align="center"><CancelIcon className="icon-cross" /></TableCell>
                                            <TableCell align="center" className="vetra-ai-column"><CheckCircleIcon className="icon-check" /></TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" scope="row">{t('howWeAreDifferentPage.comparison.table.body.detectsManipulation')}</TableCell>
                                            <TableCell align="center"><CancelIcon className="icon-cross" /></TableCell>
                                            <TableCell align="center"><CancelIcon className="icon-cross" /></TableCell>
                                            <TableCell align="center" className="vetra-ai-column"><CheckCircleIcon className="icon-check" /></TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" scope="row">{t('howWeAreDifferentPage.comparison.table.body.providesRiskScores')}</TableCell>
                                            <TableCell align="center">{t('howWeAreDifferentPage.comparison.table.body.placementOnly')}</TableCell>
                                            <TableCell align="center"><CancelIcon className="icon-cross" /></TableCell>
                                            <TableCell align="center" className="vetra-ai-column"><CheckCircleIcon className="icon-check" /></TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" scope="row">{t('howWeAreDifferentPage.comparison.table.body.createsAuditTrail')}</TableCell>
                                            <TableCell align="center"><CancelIcon className="icon-cross" /></TableCell>
                                            <TableCell align="center"><CancelIcon className="icon-cross" /></TableCell>
                                            <TableCell align="center" className="vetra-ai-column"><CheckCircleIcon className="icon-check" /></TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" scope="row">
                                                {t('howWeAreDifferentPage.comparison.table.body.survivesSocialMedia', "Survives Social Media Stripping ('The Social Media Wash')?")}
                                            </TableCell>
                                            <TableCell align="center"><CancelIcon className="icon-cross" /></TableCell>
                                            <TableCell align="center"><CancelIcon className="icon-cross" /></TableCell>
                                            <TableCell align="center" className="vetra-ai-column"><CheckCircleIcon className="icon-check" /></TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" scope="row">
                                                {t('howWeAreDifferentPage.comparison.table.body.proactiveMonitoring', "Proactive Threat Detection (Scam & Impersonation Monitoring)?")}
                                            </TableCell>
                                            <TableCell align="center">{t('howWeAreDifferentPage.comparison.table.body.limited', "Limited")}</TableCell>
                                            <TableCell align="center"><CancelIcon className="icon-cross" /></TableCell>
                                            <TableCell align="center" className="vetra-ai-column"><CheckCircleIcon className="icon-check" /></TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" scope="row">{t('howWeAreDifferentPage.comparison.table.body.providesDueDiligence')}</TableCell>
                                            <TableCell align="center">{t('howWeAreDifferentPage.comparison.table.body.limited')}</TableCell>
                                            <TableCell align="center">{t('howWeAreDifferentPage.comparison.table.body.limited')}</TableCell>
                                            <TableCell align="center" className="vetra-ai-column"><CheckCircleIcon className="icon-check" /></TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </motion.div>
                    </Box>

                    {/* Section 5: Your Unbreakable Defence */}
                    <Box className="hwd-section">
                         <Typography variant="h4" component="h2" align="center" gutterBottom>
                            {t('howWeAreDifferentPage.defence.title')}
                        </Typography>
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.5 }} variants={itemVariants}>
                            <Paper className="hwd-info-box">
                                <Avatar className="hwd-info-icon"><GavelIcon/></Avatar>
                                <Typography variant="h6" component="h3">{t('howWeAreDifferentPage.defence.quote.title')}</Typography>
                                <Typography variant="body1" sx={{ mt: 2 }}>
                                    {t('howWeAreDifferentPage.defence.quote.text')}
                                </Typography>
                            </Paper>
                        </motion.div>
                    </Box>

                </Box>
            </main>
        </div>
    );
};

export default HowWeAreDifferentPage;